// src/app.ts
var CHANTIERCARRE_WEBSITE_URL = "https://www.chantiercarre.fr";
var CHANTIERCARRE_CONTACT_URL = `${CHANTIERCARRE_WEBSITE_URL}/contact`;
var ADMIN_LIST_DEFAULT_PAGE_SIZE = 25;
var ADMIN_LIST_PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

// src/auth.ts
var AUTH_ERROR_MESSAGES = {
  /** Email is unknown, or the account was never invited or has no role. */
  EMAIL_NOT_FOUND: "Email not found",
  /** Account exists and was invited, but has been deactivated. */
  ACCOUNT_INACTIVE: "Account is inactive"
};

// src/contracts.ts
var DO_CONTRACT_TYPES = ["amo", "ct", "sps", "moe"];
var PRESTA_CONTRACT_TYPES = ["travaux_n_1", "travaux_n_2"];
var DIFFUSION_TARGET_CONTRACT_TYPES = {
  do: DO_CONTRACT_TYPES,
  presta: PRESTA_CONTRACT_TYPES
};
var MARCHE_INITIAL_FUNCTIONS = [
  "directeur_de_travaux",
  "conduc_de_travaux"
];
var CONTRACT_TYPES = [
  ...DO_CONTRACT_TYPES,
  ...PRESTA_CONTRACT_TYPES
];
var CONTRACT_TYPE_LABELS = {
  amo: "AMO",
  ct: "Contr\xF4le Technique",
  sps: "SPS",
  moe: "MOE",
  travaux_n_1: "Travaux rang 1",
  travaux_n_2: "Travaux rang 2"
};
function contractTypeLabel(type) {
  const known = CONTRACT_TYPES.find((value) => value === type);
  return known ? CONTRACT_TYPE_LABELS[known] : type;
}

// src/documents.ts
var ALLOWED_DOCUMENT_EXTENSIONS = [
  // Documents
  "pdf",
  "doc",
  "docx",
  "odt",
  "txt",
  "rtf",
  // Spreadsheets
  "xls",
  "xlsx",
  "ods",
  "csv",
  // Images
  "jpg",
  "jpeg",
  "png",
  "gif",
  "bmp",
  "tiff",
  "webp",
  "heic",
  // CAD/Technical
  "dwg",
  "dxf",
  "dwf",
  "dgn",
  "skp",
  "3dm",
  // Archives
  "zip",
  "rar",
  "7z",
  "tar",
  "gz",
  // Other
  "eml",
  "msg",
  "ppt",
  "pptx"
];
var ALLOWED_PDF_EXTENSIONS = ["pdf"];
var ALLOWED_NATIVE_EXTENSIONS = [
  "dwg",
  "dxf",
  "dwf",
  "dgn",
  "skp",
  "3dm",
  "ifc",
  "rvt",
  "pln",
  "nwd",
  "nwf",
  "nwc"
];
var MAX_PDF_SIZE_BYTES = 50 * 1024 * 1024;
var MAX_NATIVE_SIZE_BYTES = 200 * 1024 * 1024;
var RECEPTION_MODES = ["mail", "main_propre", "courrier"];
var MAX_DIFFUSION_PAPER_COPIES = 99;
var DOCUMENT_INDICES = [
  "O",
  "A",
  "B",
  "C",
  "D",
  "E",
  "F",
  "G",
  "H",
  "I",
  "J",
  "K",
  "L",
  "M",
  "N",
  "P",
  "Q",
  "R",
  "S",
  "T",
  "U",
  "V",
  "W",
  "X",
  "Y",
  "Z"
];
var FORECAST_DOCUMENT_INDEX = "PREVI";
var MAX_DOCUMENT_SIZE_BYTES = 100 * 1024 * 1024;
var MULTIPART_UPLOAD_OVERHEAD_BYTES = 10 * 1024 * 1024;
var UPLOAD_BODY_LIMIT_BYTES = MAX_DOCUMENT_SIZE_BYTES + MULTIPART_UPLOAD_OVERHEAD_BYTES;
var FICHE_QUESTION_MAX_TOTAL_UPLOAD_BYTES = 250 * 1024 * 1024;
var FICHE_QUESTION_UPLOAD_BODY_LIMIT_BYTES = FICHE_QUESTION_MAX_TOTAL_UPLOAD_BYTES + MULTIPART_UPLOAD_OVERHEAD_BYTES;
var COMPTE_RENDU_MAX_TOTAL_UPLOAD_BYTES = 250 * 1024 * 1024;
var COMPTE_RENDU_UPLOAD_BODY_LIMIT_BYTES = COMPTE_RENDU_MAX_TOTAL_UPLOAD_BYTES + MULTIPART_UPLOAD_OVERHEAD_BYTES;
var CONSIGNE_MAX_TOTAL_UPLOAD_BYTES = 250 * 1024 * 1024;
var CONSIGNE_UPLOAD_BODY_LIMIT_BYTES = CONSIGNE_MAX_TOTAL_UPLOAD_BYTES + MULTIPART_UPLOAD_OVERHEAD_BYTES;
var DOWNLOAD_URL_EXPIRY_SECONDS = 3600;
var DOCUMENT_LIST_SCREEN_KEY = "documents-execution-list";
var BIM_MODEL_LIST_SCREEN_KEY = "bim-models-execution-list";
var DOCUMENT_NATURE_LABELS = {
  plan: "Plan",
  note_calcul: "Note de calcul",
  fiche_technique: "Fiche technique",
  schema_de_principe: "Sch\xE9ma de principe",
  proces_verbal: "Proc\xE8s-verbal",
  nomenclature: "Nomenclature",
  cctp: "CCTP",
  compte_rendu: "Compte-rendu",
  notice: "Notice",
  divers: "Divers",
  paq: "PAQ",
  maquette: "Maquette"
};
var DOCUMENT_NATURE_OPTIONS = Object.entries(
  DOCUMENT_NATURE_LABELS
).map(([value, label]) => ({ label, value }));
function documentNatureLabel(nature) {
  return DOCUMENT_NATURE_LABELS[nature] ?? nature;
}

// src/domain.ts
var BASE_DOMAIN = "etudescarre.test";

// src/employees.ts
var INVALID_EMAIL_MESSAGE = "Adresse mail invalide : saisissez une adresse de la forme nom@societe.fr.";
var EMPLOYEE_ROLE_LABELS = {
  root: "Root",
  admin: "Administrateur",
  user: "Utilisateur"
};
var NO_ROLE_LABEL = "Aucun r\xF4le";
var EMPLOYEE_FUNCTION_LABELS = {
  directeur_de_travaux: "Directeur de travaux",
  conduc_de_travaux: "Conducteur de travaux",
  secretariat: "Secr\xE9tariat",
  charge_d_affaire: "Charg\xE9 d'affaires",
  chef_chantier: "Chef de chantier"
};
var TRAVAUX_N1_EMPLOYEE_FUNCTIONS = [
  "directeur_de_travaux",
  "conduc_de_travaux",
  "secretariat",
  "chef_chantier"
];
var TRAVAUX_N2_EMPLOYEE_FUNCTIONS = [
  "conduc_de_travaux",
  "chef_chantier"
];
var OVERSIGHT_EMPLOYEE_FUNCTIONS = [
  "charge_d_affaire",
  "secretariat"
];
var EMPLOYEE_FUNCTIONS_BY_CONTRACT_TYPE = {
  travaux_n_1: TRAVAUX_N1_EMPLOYEE_FUNCTIONS,
  travaux_n_2: TRAVAUX_N2_EMPLOYEE_FUNCTIONS,
  amo: OVERSIGHT_EMPLOYEE_FUNCTIONS,
  ct: OVERSIGHT_EMPLOYEE_FUNCTIONS,
  sps: OVERSIGHT_EMPLOYEE_FUNCTIONS,
  moe: OVERSIGHT_EMPLOYEE_FUNCTIONS
};

// src/local.ts
var LOCAL_BASE_DOMAIN = "etudescarre.test";

// src/logo.ts
var LOGO_ALLOWED_MIME_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp"
];
var LOGO_MAX_UPLOAD_SIZE_BYTES = 5 * 1024 * 1024;
var LOGO_TARGET_MAX_PX = 512;

// src/password.ts
var PASSWORD_MIN_LENGTH = 8;
var PASSWORD_RULES = {
  minLength: 8,
  requireUppercase: true,
  requireLowercase: true,
  requireNumber: true
};
var PASSWORD_ERROR_MESSAGES = {
  tooShort: "Le mot de passe doit contenir au moins 8 caract\xE8res",
  missingUppercase: "Le mot de passe doit contenir au moins une majuscule",
  missingLowercase: "Le mot de passe doit contenir au moins une minuscule",
  missingNumber: "Le mot de passe doit contenir au moins un chiffre"
};

// src/reports.ts
var NOTICE_MAPPING_DEFAVORABLE = "defavorable";
var NOTICE_MAPPING_LABELS = {
  favorable: "Favorable",
  defavorable: "D\xE9favorable",
  vu_avec_observation: "Vu avec observation",
  vu_sans_observation: "Vu sans observation"
};
function noticeStatusLabel(status) {
  const mapping = status?.mappingChantierCarre;
  return mapping ? NOTICE_MAPPING_LABELS[mapping] ?? mapping : status?.valeurCompany ?? null;
}

// src/session.ts
var IDLE_TIMEOUT_MS = 30 * 60 * 1e3;
var IDLE_WARNING_MS = 60 * 1e3;

// src/yards.ts
var YARD_EXPIRATION_MIN_YEAR = 1900;
var YARD_EXPIRATION_MAX_YEARS_AHEAD = 50;
var INTERVENTION_DATE_MIN = "2000-01-01";
var INTERVENTION_DATE_MAX = "2099-12-31";
var YARD_ACCESS_DENIALS = [
  "yard_not_commissioned",
  "yard_off",
  "account_inactive",
  "not_on_yard"
];
var YARD_STATE_LABELS = {
  preparation: "En pr\xE9paration",
  en_service: "En service",
  interrompu: "Interrompu",
  archive: "Archiv\xE9",
  cloture: "Cl\xF4tur\xE9"
};

export { ADMIN_LIST_DEFAULT_PAGE_SIZE, ADMIN_LIST_PAGE_SIZE_OPTIONS, ALLOWED_DOCUMENT_EXTENSIONS, ALLOWED_NATIVE_EXTENSIONS, ALLOWED_PDF_EXTENSIONS, AUTH_ERROR_MESSAGES, BASE_DOMAIN, BIM_MODEL_LIST_SCREEN_KEY, CHANTIERCARRE_CONTACT_URL, CHANTIERCARRE_WEBSITE_URL, COMPTE_RENDU_MAX_TOTAL_UPLOAD_BYTES, COMPTE_RENDU_UPLOAD_BODY_LIMIT_BYTES, CONSIGNE_MAX_TOTAL_UPLOAD_BYTES, CONSIGNE_UPLOAD_BODY_LIMIT_BYTES, CONTRACT_TYPES, CONTRACT_TYPE_LABELS, DIFFUSION_TARGET_CONTRACT_TYPES, DOCUMENT_INDICES, DOCUMENT_LIST_SCREEN_KEY, DOCUMENT_NATURE_LABELS, DOCUMENT_NATURE_OPTIONS, DOWNLOAD_URL_EXPIRY_SECONDS, DO_CONTRACT_TYPES, EMPLOYEE_FUNCTIONS_BY_CONTRACT_TYPE, EMPLOYEE_FUNCTION_LABELS, EMPLOYEE_ROLE_LABELS, FICHE_QUESTION_MAX_TOTAL_UPLOAD_BYTES, FICHE_QUESTION_UPLOAD_BODY_LIMIT_BYTES, FORECAST_DOCUMENT_INDEX, IDLE_TIMEOUT_MS, IDLE_WARNING_MS, INTERVENTION_DATE_MAX, INTERVENTION_DATE_MIN, INVALID_EMAIL_MESSAGE, LOCAL_BASE_DOMAIN, LOGO_ALLOWED_MIME_TYPES, LOGO_MAX_UPLOAD_SIZE_BYTES, LOGO_TARGET_MAX_PX, MARCHE_INITIAL_FUNCTIONS, MAX_DIFFUSION_PAPER_COPIES, MAX_DOCUMENT_SIZE_BYTES, MAX_NATIVE_SIZE_BYTES, MAX_PDF_SIZE_BYTES, MULTIPART_UPLOAD_OVERHEAD_BYTES, NOTICE_MAPPING_DEFAVORABLE, NOTICE_MAPPING_LABELS, NO_ROLE_LABEL, OVERSIGHT_EMPLOYEE_FUNCTIONS, PASSWORD_ERROR_MESSAGES, PASSWORD_MIN_LENGTH, PASSWORD_RULES, PRESTA_CONTRACT_TYPES, RECEPTION_MODES, TRAVAUX_N1_EMPLOYEE_FUNCTIONS, TRAVAUX_N2_EMPLOYEE_FUNCTIONS, UPLOAD_BODY_LIMIT_BYTES, YARD_ACCESS_DENIALS, YARD_EXPIRATION_MAX_YEARS_AHEAD, YARD_EXPIRATION_MIN_YEAR, YARD_STATE_LABELS, contractTypeLabel, documentNatureLabel, noticeStatusLabel };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map